import { withStyles } from '@material-ui/core/styles'
import ToggleButton from '@material-ui/lab/ToggleButton'
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup'
import cn from 'classnames'
import { isNil } from 'lodash'
import React from 'react'

import Flag from '../Flag'
import LabelWrapper from '../LabelWrapper'

const styles = theme => ({
  toggleGroup: {
    display: 'flex',
    boxShadow: 'none',
    background: 'transparent',
    '& > button:first-child': {
      borderLeftWidth: '1px',
      borderTopLeftRadius: '4px',
      borderBottomLeftRadius: '4px',
    },
    '& > button:last-child': {
      borderTopRightRadius: '4px',
      borderBottomRightRadius: '4px',
    },
    '& > button:not(:first-child)': {
      borderLeftColor: `var(--gameday-borders-borderColor, ${theme.borders.borderColor})`,
    },

    '&.fullWidthToggle': {
      '& > button': { flexGrow: 1 },
    },
  },
  root: {
    height: '36px',
    padding: '0 16px',
    textTransform: 'none',
    fontWeight: 'normal',
    backgroundColor: `var(--gameday-inputField-inputBGColor, ${theme.inputField.inputBGColor})`,
    border: `1px solid ${`var(--gameday-inputField-inputBorder, ${theme.inputField.inputBorder})`} !important`,
    borderLeftWidth: '0px',
    borderRadius: '0px',
    color: `var(--gameday-inputField-inputTextColor, ${theme.inputField.inputTextColor})`,
    letterSpacing: 'initial',
    '& > span': {
      lineHeight: '1 !important',
    },
    '&.fullWidth': {
      width: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '&:disabled': {
      backgroundColor: `var(--gameday-tabs-toggleGroupDisabledColor, ${theme.tabs.toggleGroupDisabledBackgroundColorDarkMode})`,
      color: `var(--gameday-inputField-inputTextColor, ${theme.inputField.inputTextColor})`,
      opacity: 0.5,
    },
  },
  selected: {
    backgroundColor: `${theme.buttons.primaryButtonBg} !important`,
    borderColor: `${theme.buttons.primaryButtonBg} !important`,
    color: `${theme.colors.white} !important`,
    '&:after': {
      opacity: 0,
    },
    '&:hover': {
      backgroundColor: `${theme.buttons.primaryButtonBgHover} !important`,
      borderColor: `${theme.buttons.primaryButtonBgHover} !important`,
    },
    '&:disabled': {
      backgroundColor: `${theme.buttons.primaryButtonBg} !important`,
      borderColor: `${theme.buttons.primaryButtonBg} !important`,
      color: `${theme.colors.white} !important`,
      opacity: 0.6,
    },
    '&.success': {
      backgroundColor: `${theme.status.successColor} !important`,
      borderColor: `${theme.status.successColor} !important`,
    },
    '&.error': {
      backgroundColor: `${theme.status.errorColor} !important`,
      borderColor: `${theme.status.errorColor} !important`,
    },
    '&.warning': {
      backgroundColor: `${theme.status.warningColor} !important`,
      borderColor: `${theme.status.warningColor} !important`,
    },
    '&.info': {
      backgroundColor: `${theme.status.infoColor} !important`,
      borderColor: `${theme.status.infoColor} !important`,
    },
  },
  flag: {
    marginLeft: '0.5rem',
  },
})

class ToggleGroup extends React.Component {
  handleOption = (e, value) => {
    const { onChange } = this.props
    e.stopPropagation()
    if (!isNil(value)) {
      onChange(value)
    }
  }

  render() {
    const {
      classes,
      className,
      customCol,
      customMargin,
      customPadding,
      customWidth,
      disabled,
      errorText,
      fullWidth,
      fullWidthToggle,
      helperText,
      id,
      isLabelInline,
      isRequired,
      isToolTip,
      label,
      multipleFields,
      noMobile,
      notWrapper,
      noWrap,
      spacing,
      toolTipIcon,
      toolTipIconColor,
      toolTipIconSize,
      toolTipPosition,
      toolTipText,
      toggles,
      withBorder,
      withDivider,
      flag,
      flagItem,
      flagToolTip,
      flagPlacement,
      ...rest
    } = this.props

    return (
      <LabelWrapper
        className={cn(className, 'toggle-group')}
        customCol={customCol}
        customMargin={customMargin}
        customPadding={customPadding}
        customWidth={customWidth}
        disabled={disabled}
        errorText={errorText}
        fullWidth={fullWidth}
        helperText={helperText}
        id={id}
        isLabelInline={isLabelInline}
        isRequired={isRequired}
        isToolTip={isToolTip}
        label={label}
        multipleFields={multipleFields}
        noMobile={noMobile}
        notWrapper={notWrapper}
        noWrap={noWrap}
        spacing={spacing}
        toolTipIcon={toolTipIcon}
        toolTipIconColor={toolTipIconColor}
        toolTipIconSize={toolTipIconSize}
        toolTipPosition={toolTipPosition}
        toolTipText={toolTipText}
        withBorder={withBorder}
        withDivider={withDivider}
        flag={flag}
        flagToolTip={flagToolTip}
        flagPlacement={flagPlacement}
        {...rest}
      >
        <ToggleButtonGroup
          value={rest.default}
          exclusive
          onChange={this.handleOption}
          className={cn(classes.toggleGroup, fullWidthToggle && ' fullWidthToggle')}
          style={customWidth ? { width: `${customWidth}px` } : {}}
        >
          {toggles.map((toggle, i) => (
            <ToggleButton
              key={toggle.value}
              classes={{
                root: classes.root,
                selected: classes.selected,
              }}
              className={toggle.variant ? toggle.variant : 'default'}
              value={toggle.value}
              disabled={disabled}
              aria-label={toggle.label}
              data-testid={`toggleButton-${toggle.value}`}
            >
              {toggle.label}
              {flag && flagPlacement && flagPlacement === 'button' && flagItem && flagItem === i && (
                <Flag
                  variant={flag}
                  title={flag}
                  showToolTip
                  toolTipText={flagToolTip}
                  style={{ marginLeft: '10px' }}
                />
              )}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </LabelWrapper>
    )
  }
}

ToggleGroup.defaultProps = {
  default: '',
  fullWidth: false,
  fullWidthToggle: false,
  notWrapper: true,
  withBorder: true,
}

const ToggleGroupComponent = withStyles(styles)(ToggleGroup)
ToggleGroupComponent.displayName = 'ToggleGroup'
export default ToggleGroupComponent
